import type { ReactNode } from 'react';

/**
 * Platform-faithful post previews (§20.3). The Preview surface exists so an
 * operator sees a post EXACTLY as the target platform will render it — the real
 * chrome (avatar, name, timestamp, engagement bar), not a generic card. Each
 * frame is a deliberate island of that platform's own light UI, so it reads as
 * "this is what lands on LinkedIn / Instagram / X", regardless of the app theme.
 *
 * Frames are approximations of each platform's feed card, close enough to judge
 * how copy and media will look before anything goes out.
 */

export interface PostContent {
  /** The connector key, e.g. 'linkedin', 'meta-graph', 'twitter'. */
  readonly platform: string;
  /** Who the post publishes as — the Page / account / channel name. */
  readonly authorName: string;
  readonly handle?: string | null;
  readonly title?: string | null;
  readonly body?: string | null;
  readonly mediaUrl?: string | null;
  /** Human-readable time, already formatted. */
  readonly timestamp?: string | null;
}

type Kind = 'linkedin' | 'x' | 'instagram' | 'facebook' | 'pinterest' | 'sandbox' | 'generic';

/** Map a connector key onto the platform frame that renders it faithfully. */
export function platformKind(connectorKey: string): Kind {
  switch (connectorKey) {
    case 'linkedin':
      return 'linkedin';
    case 'twitter':
      return 'x';
    case 'threads':
      return 'x';
    case 'meta-graph':
    case 'instagram':
      return 'instagram';
    case 'facebook':
      return 'facebook';
    case 'pinterest':
      return 'pinterest';
    case 'sandbox-channel':
      return 'sandbox';
    default:
      return 'generic';
  }
}

const KIND_LABEL: Record<Kind, string> = {
  linkedin: 'LinkedIn',
  x: 'X',
  instagram: 'Instagram',
  facebook: 'Facebook',
  pinterest: 'Pinterest',
  sandbox: 'Sandbox',
  generic: 'Preview',
};

export function platformLabel(connectorKey: string): string {
  return KIND_LABEL[platformKind(connectorKey)];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]!}${parts[parts.length - 1]![0]!}`.toUpperCase();
}

// --- tiny, faithful engagement glyphs (stroke icons keep every platform crisp) ---
function Glyph({ d, filled = false }: { d: string; filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
const ICON = {
  like: 'M7 10v11M2 13v6a2 2 0 0 0 2 2h13.5a2 2 0 0 0 2-1.6l1.4-7A2 2 0 0 0 18 10h-5l1-4a2 2 0 0 0-2-2l-5 6',
  comment: 'M21 11.5a8.5 8.5 0 0 1-12.3 7.6L3 21l1.9-5.7A8.5 8.5 0 1 1 21 11.5z',
  repost: 'M17 2l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z',
  heart:
    'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z',
  bookmark: 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z',
  share: 'M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v14',
  globe:
    'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z',
} as const;

/** Body text with a platform-styled "…more" affordance implied by clamping. */
function Body({
  text,
  className,
  clamp,
}: {
  text: string;
  className: string;
  clamp?: number | undefined;
}) {
  const clampClass =
    clamp === 3 ? 'line-clamp-3' : clamp === 4 ? 'line-clamp-4' : clamp === 5 ? 'line-clamp-5' : '';
  return <p className={`${className} whitespace-pre-line ${clampClass}`}>{text}</p>;
}

/** A Tailwind text size proportional to the avatar diameter (no inline font-size). */
function avatarTextClass(px: number): string {
  return px >= 44 ? 'text-base' : px >= 36 ? 'text-sm' : px >= 28 ? 'text-xs' : 'text-[10px]';
}

function MediaBox({
  mediaUrl,
  alt,
  aspectClass,
  rounded,
  fallback,
}: {
  mediaUrl: string | null | undefined;
  alt: string;
  aspectClass: string;
  rounded?: string;
  fallback?: ReactNode;
}) {
  return (
    <div
      className={`flex items-center justify-center overflow-hidden bg-[#f1f2f4] ${aspectClass} ${rounded ?? ''}`}
    >
      {mediaUrl !== null && mediaUrl !== undefined ? (
        <img src={mediaUrl} alt={alt} className="h-full w-full object-cover" />
      ) : (
        (fallback ?? <span className="text-xs text-[#8a8f98]">No media</span>)
      )}
    </div>
  );
}

/** A text-only creative rendered as a branded tile, for image-first platforms.
    This is INTEGRA's OWN surface (not a third-party platform frame), so it wears
    the brand navy via design tokens rather than raw hex. */
function TextTile({ title, body }: { title: string | null; body: string | null }) {
  return (
    <div className="flex h-full w-full flex-col justify-center gap-2 bg-gradient-to-br from-navy-700 to-navy-900 px-6 py-5 text-left">
      {title !== null && (
        <p className="line-clamp-3 text-lg font-bold leading-snug text-ink-inverse">{title}</p>
      )}
      {body !== null && (
        <p className="line-clamp-4 text-sm leading-relaxed text-navy-100">{body}</p>
      )}
    </div>
  );
}

const size = { card: 'w-full', full: 'mx-auto w-full max-w-[540px]' } as const;

/**
 * Render a post as its target platform's feed card. `variant` controls density:
 * `card` for the grid, `full` for the click-through detail.
 */
export function PlatformPreview({
  content,
  variant = 'card',
}: {
  content: PostContent;
  variant?: 'card' | 'full';
}) {
  const kind = platformKind(content.platform);
  const author = content.authorName || KIND_LABEL[kind];
  const body = content.body ?? content.title ?? '';
  const time = content.timestamp ?? '';
  const wrap = size[variant];

  if (kind === 'linkedin') {
    return (
      <div className={`overflow-hidden rounded-lg border border-[#e0e0e0] bg-white ${wrap}`}>
        <div className="flex items-start gap-2 px-4 pt-3">
          <Avatar
            name={author}
            className="bg-[#0a66c2] text-white"
            size={48}
            rounded="rounded-full"
          />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-semibold text-[#000000e6]">{author}</p>
            {content.handle != null && (
              <p className="truncate text-xs text-[#00000099]">{content.handle}</p>
            )}
            <p className="mt-0.5 flex items-center gap-1 text-xs text-[#00000099]">
              {time || 'Just now'} ·
              <span className="text-[#00000099]">
                <Glyph d={ICON.globe} />
              </span>
            </p>
          </div>
          <span className="text-lg font-bold text-[#00000099]">···</span>
        </div>
        {body !== '' && (
          <Body
            text={body}
            className="px-4 pb-2 pt-2 text-sm leading-[1.4] text-[#000000e6]"
            clamp={variant === 'card' ? 4 : undefined}
          />
        )}
        {(content.mediaUrl != null || variant === 'full') && (
          <MediaBox mediaUrl={content.mediaUrl} alt={author} aspectClass="aspect-[1.91/1]" />
        )}
        <div className="flex items-center justify-between px-3 py-1.5">
          {(['like', 'comment', 'repost', 'send'] as const).map((k) => (
            <span
              key={k}
              className="flex flex-1 items-center justify-center gap-1.5 py-1.5 text-[13px] font-medium capitalize text-[#00000099]"
            >
              <Glyph d={ICON[k]} />
              {k === 'repost' ? 'Repost' : k}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'x') {
    return (
      <div className={`rounded-2xl border border-[#e1e8ed] bg-white px-4 py-3 ${wrap}`}>
        <div className="flex gap-3">
          <Avatar
            name={author}
            className="bg-[#0f1419] text-white"
            size={44}
            rounded="rounded-full"
          />
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1 text-sm leading-tight">
              <span className="truncate font-bold text-[#0f1419]">{author}</span>
              <span className="truncate text-[#536471]">
                {content.handle ?? `@${author.toLowerCase().replace(/\s+/g, '')}`}
                {time !== '' ? ` · ${time}` : ''}
              </span>
            </p>
            {body !== '' && (
              <Body
                text={body}
                className="mt-0.5 text-[15px] leading-[1.35] text-[#0f1419]"
                clamp={variant === 'card' ? 5 : undefined}
              />
            )}
            {content.mediaUrl != null && (
              <div className="mt-2">
                <MediaBox
                  mediaUrl={content.mediaUrl}
                  alt={author}
                  aspectClass="aspect-video"
                  rounded="rounded-2xl border border-[#e1e8ed]"
                />
              </div>
            )}
            <div className="mt-2 flex max-w-[320px] items-center justify-between text-[#536471]">
              {(['comment', 'repost', 'heart', 'bookmark', 'share'] as const).map((k) => (
                <span key={k}>
                  <Glyph d={ICON[k]} />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (kind === 'instagram') {
    return (
      <div className={`overflow-hidden rounded-lg border border-[#dbdbdb] bg-white ${wrap}`}>
        <div className="flex items-center gap-2 px-3 py-2.5">
          <span className="rounded-full bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] p-[2px]">
            <Avatar
              name={author}
              className="bg-white text-[#262626]"
              size={30}
              rounded="rounded-full"
              ring
            />
          </span>
          <span className="flex-1 truncate text-sm font-semibold text-[#262626]">
            {content.handle ?? author}
          </span>
          <span className="text-sm font-bold text-[#262626]">···</span>
        </div>
        <MediaBox
          mediaUrl={content.mediaUrl}
          alt={author}
          aspectClass="aspect-square"
          fallback={<TextTile title={content.title ?? null} body={content.body ?? null} />}
        />
        <div className="flex items-center gap-4 px-3 pt-2.5 text-[#262626]">
          <Glyph d={ICON.heart} />
          <Glyph d={ICON.comment} />
          <Glyph d={ICON.send} />
          <span className="ml-auto">
            <Glyph d={ICON.bookmark} />
          </span>
        </div>
        {body !== '' && (
          <div className="px-3 pb-3 pt-2">
            <Body
              text={body}
              className="text-sm leading-[1.35] text-[#262626]"
              clamp={variant === 'card' ? 3 : undefined}
            />
          </div>
        )}
        {time !== '' && (
          <p className="px-3 pb-3 text-[10px] uppercase tracking-wide text-[#8e8e8e]">{time}</p>
        )}
      </div>
    );
  }

  if (kind === 'facebook') {
    return (
      <div className={`overflow-hidden rounded-lg border border-[#dddfe2] bg-white ${wrap}`}>
        <div className="flex items-center gap-2 px-3 pt-3">
          <Avatar
            name={author}
            className="bg-[#1877f2] text-white"
            size={40}
            rounded="rounded-full"
          />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-semibold text-[#050505]">{author}</p>
            <p className="flex items-center gap-1 text-xs text-[#65676b]">
              {time || 'Just now'} ·
              <span>
                <Glyph d={ICON.globe} />
              </span>
            </p>
          </div>
          <span className="text-lg font-bold text-[#65676b]">···</span>
        </div>
        {body !== '' && (
          <Body
            text={body}
            className="px-3 py-2 text-[15px] leading-[1.4] text-[#050505]"
            clamp={variant === 'card' ? 4 : undefined}
          />
        )}
        {(content.mediaUrl != null || variant === 'full') && (
          <MediaBox mediaUrl={content.mediaUrl} alt={author} aspectClass="aspect-[1.91/1]" />
        )}
        <div className="mx-3 flex items-center justify-around border-t border-[#dddfe2] py-1">
          {(['like', 'comment', 'share'] as const).map((k) => (
            <span
              key={k}
              className="flex flex-1 items-center justify-center gap-1.5 py-1.5 text-[13px] font-semibold capitalize text-[#65676b]"
            >
              <Glyph d={ICON[k]} />
              {k}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'pinterest') {
    return (
      <div className={`overflow-hidden rounded-2xl border border-[#e9e9e9] bg-white ${wrap}`}>
        <MediaBox
          mediaUrl={content.mediaUrl}
          alt={author}
          aspectClass="aspect-[2/3]"
          fallback={<TextTile title={content.title ?? null} body={content.body ?? null} />}
        />
        <div className="px-3 py-2.5">
          {content.title != null && (
            <p className="line-clamp-2 text-sm font-semibold text-[#111]">{content.title}</p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <Avatar
              name={author}
              className="bg-[#e60023] text-white"
              size={24}
              rounded="rounded-full"
            />
            <span className="truncate text-xs text-[#5f5f5f]">{author}</span>
          </div>
        </div>
      </div>
    );
  }

  // sandbox / generic — INTEGRA's OWN neutral frame (not a third-party platform),
  // so it is built from the design tokens: brand navy + garnet, never heritage gold.
  return (
    <div className={`overflow-hidden rounded-lg border border-edge bg-surface ${wrap}`}>
      <div className="flex items-center gap-2 border-b border-edge px-4 py-2.5">
        <Avatar
          name={author}
          className="bg-navy-700 text-gold-500"
          size={32}
          rounded="rounded-md"
        />
        <span className="flex-1 truncate text-sm font-semibold text-navy-700">{author}</span>
        {kind === 'sandbox' && (
          <span className="rounded-full bg-gold-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold-text">
            Sandbox
          </span>
        )}
      </div>
      <MediaBox
        mediaUrl={content.mediaUrl}
        alt={author}
        aspectClass="aspect-[4/3]"
        fallback={<TextTile title={content.title ?? null} body={content.body ?? null} />}
      />
      {body !== '' && (
        <Body
          text={body}
          className="px-4 py-3 text-sm leading-[1.4] text-ink"
          clamp={variant === 'card' ? 4 : undefined}
        />
      )}
    </div>
  );
}

function Avatar({
  name,
  className,
  size: px,
  rounded,
  ring,
}: {
  name: string;
  className: string;
  size: number;
  rounded: string;
  ring?: boolean;
}) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center font-bold ${avatarTextClass(px)} ${rounded} ${className} ${ring ? 'ring-2 ring-white' : ''}`}
      style={{ width: px, height: px }}
    >
      {initials(name)}
    </span>
  );
}
